import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Icon, IconChatLock, IconRaisedHandSlash } from '../../base/icons';
import { PERMISSIONS, VISITOR_ROLE, getEffectiveRosterPermissions } from '../../base/permissions';
import { Tooltip } from '../../base/tooltip';
import { getPromotionRequests, getVisitorsRoster } from '../../visitors/functions';
import { ACTION_TRIGGER } from '../constants';
import { findStyledAncestor } from '../functions';
import { ParticipantItem } from './ParticipantItem';
import VisitorContextMenu from './VisitorContextMenu';
import { useModerationStyles } from './moderation/styles';
import { ParticipantContainer, SearchParticipantInput } from './styled';
const listStyle = {
    maxHeight: '50vh',
    overflowX: 'hidden',
    overflowY: 'auto'
};
/**
 * Server-reported visitors, using the same rows and menu as participants.
 *
 * @returns {ReactNode}
 */
export default function VisitorsRoster() {
    const { t } = useTranslation();
    const { classes } = useModerationStyles();
    const roster = useSelector(getVisitorsRoster);
    const requests = useSelector(getPromotionRequests);
    const suspendedChatVids = useSelector((state) => state['features/visitors'].suspendedChatVids);
    const [menuContext, setMenuContext] = useState(null);
    const [searchString, setSearchString] = useState('');
    const closeMenu = useCallback(() => setMenuContext(null), []);
    const visitors = roster.filter(visitor => (visitor.nick || t('webinar.backstageUnnamed'))
        .toLowerCase().includes(searchString.toLowerCase()));
    const toggleMenu = useCallback((vid, target) => {
        const offsetTarget = findStyledAncestor(target, ParticipantContainer);
        if (!(offsetTarget instanceof HTMLElement)) {
            setMenuContext(null);
            return;
        }
        setMenuContext(current => (current === null || current === void 0 ? void 0 : current.vid) === vid ? null : {
            offsetTarget,
            vid
        });
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement(SearchParticipantInput, { "aria-label": t('participantsPane.search'), id: 'visitors-search-input', onChange: e => {
                setSearchString(e.target.value);
                closeMenu();
            }, placeholder: t('participantsPane.search'), type: 'text', value: searchString }),
        React.createElement("div", { style: listStyle },
            visitors.map((visitor, index) => {
                const chatSuspended = Boolean(suspendedChatVids === null || suspendedChatVids === void 0 ? void 0 : suspendedChatVids[visitor.vid]);
                const permissions = getEffectiveRosterPermissions(VISITOR_ROLE);
                permissions[PERMISSIONS.SEND_GROUPCHAT] = !chatSuspended;
                return (React.createElement(ParticipantItem, { actionsTrigger: ACTION_TRIGGER.HOVER, ariaLabel: t('toolbar.moreOptions'), displayName: visitor.nick
                        || t('webinar.backstageUnnamed'), index: index + 1, indicators: React.createElement(React.Fragment, null,
                        chatSuspended && (React.createElement(Tooltip, { className: 'small', content: 'participantsPane.actions.chatBlockedBadge', position: index === 0
                                ? 'bottom-right' : 'top-centre' },
                            React.createElement("div", { className: 'moderator-icon-participants participantpane-item-icons' },
                                React.createElement(Icon, { src: IconChatLock })))),
                        visitor.handBlocked && (React.createElement(Tooltip, { className: 'small', content: 'participantsPane.actions.handBlockedBadge', position: index === 0
                                ? 'bottom-right' : 'top-centre' },
                            React.createElement("div", { className: 'moderator-icon-participants participantpane-item-icons' },
                                React.createElement(Icon, { src: IconRaisedHandSlash }))))), isHighlighted: (menuContext === null || menuContext === void 0 ? void 0 : menuContext.vid) === visitor.vid, isModerator: false, key: visitor.vid, onContextMenu: (e) => toggleMenu(visitor.vid, e.target), participant: {
                        id: visitor.jid,
                        raisedHand: requests.some(request => request.from === visitor.jid),
                        role: VISITOR_ROLE
                    }, participantList: visitors, permissions: permissions, role: VISITOR_ROLE }));
            }),
            !roster.length && (React.createElement("span", { className: classes.rosterEmpty }, t('participantsPane.headings.visitorsEmpty')))),
        menuContext && (React.createElement(VisitorContextMenu, { offsetTarget: menuContext.offsetTarget, onSelect: closeMenu, vid: menuContext.vid }))));
}
